import React, { useState, useEffect } from "react";
import Select from "react-select";
import { FormFeedback, Tooltip } from "reactstrap";

const DropdownInput = ({
    label = "Select Option", // Default label
    required = false, // Is the field required?
    layout = "below", // Layout style (inline or below)
    options = [], // Dropdown options array
    defaultSelectedKey = "", // The key of the default selected option
    name = "dropdown", // Input name
    id = "dropdownInput", // Input ID
    onChange = () => {}, // onChange handler
    onBlur = () => {}, // onBlur handler
    requiredErrorMessage = "You must select at least one option.", // Default error message
    placeholder = "Please select an option", // Placeholder for dropdown
    showHelp = false, // Show help icon?
    helpText = "Please select an option.", // Help tooltip text
    isClearable = true, // Enable clear (close) button
    validateTrigger = false, // Trigger external validation
    disabled = false // Add the disabled prop with default false
}) => {
    const [selectedValue, setSelectedValue] = useState(null); // Selected value state
    const [errors, setErrors] = useState({}); // Error state
    const [tooltipOpen, setTooltipOpen] = useState(false); // Tooltip state

    // Set default selected value based on the defaultSelectedKey prop
    useEffect(() => {
        const defaultSelectedOption = options.find(option => option.value === defaultSelectedKey);
        setSelectedValue(defaultSelectedOption || null); // Set the default option or null if not found
    }, [defaultSelectedKey, options]);

    // Validation function
    const validateInput = (value) => {
        const newErrors = {};
        if (required && !value) {
            newErrors.required = requiredErrorMessage;
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0; // Return true if no errors
    };

    // Validate input if validateTrigger is true
    useEffect(() => {
        if (validateTrigger) {
            validateInput(selectedValue);
        }
    }, [validateTrigger, selectedValue]);

    // Handle option select
    const handleOptionSelect = (selectedOption) => {
        setSelectedValue(selectedOption); // Update selected value
        validateInput(selectedOption); // Validate selected value
        onChange(selectedOption); // Trigger external onChange with the selected value
    };

    return (
        <div className={layout === "inline" ? "d-flex align-items-center" : ""}>
            <label htmlFor={id} className="d-flex" style={{ width: layout === "inline" ? "20%" : "100%" }}>
                <span>{label}
                {required && <span className="text-danger ml-1">*</span>}
                </span>
                {showHelp && (
                    <span id="helpTooltip" className="px-2">
                        <i
                            className="ri-question-line"
                            style={{ cursor: 'pointer', fontSize: '16px' }}
                        />
                    </span>
                )}
            </label>
            {showHelp && (
                <Tooltip
                    placement="right"
                    isOpen={tooltipOpen}
                    target="helpTooltip"
                    toggle={() => setTooltipOpen(!tooltipOpen)}
                >
                    {helpText}
                </Tooltip>
            )}
            <div className="w-100">
                <Select
                    name={name}
                    id={id}
                    value={selectedValue} // Use the current or default selected value
                    onChange={handleOptionSelect} // Handle selection
                    onBlur={() => validateInput(selectedValue)} // Validate on blur
                    options={options} // Dropdown options
                    placeholder={placeholder} // Placeholder text
                    isSearchable // Enable search functionality
                    isClearable={isClearable} // Show close icon to clear selection
                    classNamePrefix="react-select"
                    className={errors.required ? 'is-invalid' : ''} // Add error class if validation fails
                    isDisabled={disabled} // Apply the disabled prop here
                />
                {errors.required && (
                    <FormFeedback type="invalid">
                        {errors.required}
                    </FormFeedback>
                )}
            </div>
        </div>
    );
};

export default DropdownInput;
